/**
 * Supplier Outstanding Inquiry — per-supplier outstanding balance summary.
 * Loads via getOutstandingSummary (partyType=Supplier).
 * Clicking a row drills down to Open Items filtered by that supplier.
 */
import { useState, useCallback, useEffect } from 'react';
import { useNavigate }   from 'react-router-dom';
import { useSelector }   from 'react-redux';
import {
  RefreshCw, Building, MapPin, Layers, Search, ChevronRight, Download,
} from 'lucide-react';

import { openItemApi }   from '@/api/openItem.api';
import { exportToCsv }   from '@/utils/exportCsv';
import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { AppRoutes }     from '@/constants/routes';
import { Spinner }       from '@/components/ui/Spinner';
import { cn }            from '@/utils/cn';
import type { RootState } from '@/state/store';
import type { OutstandingInquiryDto } from '@/types/openItem.types';

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

export const SupplierOutstandingPage = () => {
  const navigate           = useNavigate();
  const { hasPermission }  = usePermission();
  const canView            = hasPermission(Permissions.FIN_OPEN_ITEM_VIEW);
  const canViewAllLoc      = hasPermission(Permissions.ALLOCATION_VIEW_ALL_LOCATIONS) || hasPermission(Permissions.TXN_VIEW_ALL_LOCATIONS);
  const canViewAllCC       = hasPermission(Permissions.ALLOCATION_VIEW_ALL_COST_CENTERS) || hasPermission(Permissions.TXN_VIEW_ALL_COST_CENTERS);

  const session = useSelector((s: RootState) => s.session.session);

  const [rows,    setRows]    = useState<OutstandingInquiryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [search,  setSearch]  = useState('');

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const res = await openItemApi.getOutstandingSummary({
        partyType:    'Supplier',
        locationId:   session?.locationId   || undefined,
        costCenterId: session?.costCenterId || undefined,
        fyCode:       session?.financialYearCode || undefined,
      });
      setRows(res.data?.data ?? []);
    } catch (e) {
      console.error('Supplier outstanding load failed', e);
    } finally {
      setLoading(false);
    }
  }, [canView, session]);

  useEffect(() => { load(); }, [load]);

  const filtered = search.trim()
    ? rows.filter(r =>
        r.partyCode.toLowerCase().includes(search.toLowerCase()) ||
        r.partyName.toLowerCase().includes(search.toLowerCase())
      )
    : rows;

  const totals = filtered.reduce(
    (acc, r) => ({
      totalOriginal:    acc.totalOriginal    + r.totalOriginal,
      totalAllocated:   acc.totalAllocated   + r.totalAllocated,
      totalOutstanding: acc.totalOutstanding + r.totalOutstanding,
      openItemCount:    acc.openItemCount    + r.openItemCount,
    }),
    { totalOriginal: 0, totalAllocated: 0, totalOutstanding: 0, openItemCount: 0 }
  );

  const handleRowClick = (row: OutstandingInquiryDto) => {
    navigate(`${AppRoutes.FIN_OPEN_ITEMS}?partyType=Supplier&partyId=${row.partyId}&partyName=${encodeURIComponent(row.partyName)}`);
  };

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400 dark:text-gray-400">
        <p className="text-sm">You do not have permission to view outstanding balances.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Supplier Outstanding</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {loading ? 'Loading…' : `${filtered.length} supplier${filtered.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {filtered.length > 0 && (
              <button
                onClick={() => exportToCsv('supplier-outstanding.csv', filtered.map(r => ({
                  SupplierCode: r.partyCode, SupplierName: r.partyName,
                  FinYear: r.financialYearCode, Currency: r.currencyCode,
                  TotalOriginal: r.totalOriginal, TotalAllocated: r.totalAllocated,
                  TotalOutstanding: r.totalOutstanding, OpenItems: r.openItemCount,
                })))}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-600 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-300 rounded-lg transition-colors">
                <Download size={13} /> Export
              </button>
            )}
            <button onClick={load} disabled={loading}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors disabled:opacity-40"
              title="Refresh">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Scope chips */}
        {(session?.locationName || session?.costCenterName) && (
          <div className="flex items-center gap-2 mt-3">
            <span className="text-[10px] text-slate-400 dark:text-gray-400 font-medium uppercase tracking-wider flex items-center gap-1">
              <MapPin size={10} /> Scope:
            </span>
            {session?.locationName && (
              <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium',
                !canViewAllLoc ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 ring-1 ring-blue-200' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400 ring-1 ring-slate-200')}>
                <MapPin size={10} />{session.locationCode} — {session.locationName}
              </span>
            )}
            {session?.costCenterName && (
              <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium',
                !canViewAllCC ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 ring-1 ring-blue-200' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400 ring-1 ring-slate-200')}>
                <Layers size={10} />{session.costCenterCode} — {session.costCenterName}
              </span>
            )}
          </div>
        )}

        {/* Summary cards */}
        {!loading && filtered.length > 0 && (
          <div className="grid grid-cols-4 gap-3 mt-4">
            {[
              { label: 'Suppliers',       value: filtered.length.toString(),   color: 'text-slate-700 dark:text-white' },
              { label: 'Total Original',  value: fmt(totals.totalOriginal),    color: 'text-slate-700 dark:text-white' },
              { label: 'Total Allocated', value: fmt(totals.totalAllocated),   color: 'text-emerald-700 dark:text-emerald-400' },
              { label: 'Total Outstanding', value: fmt(totals.totalOutstanding), color: totals.totalOutstanding > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-slate-400 dark:text-gray-400' },
            ].map(c => (
              <div key={c.label} className="bg-slate-50 dark:bg-slate-700/40 rounded-lg px-4 py-3 ring-1 ring-slate-200 dark:ring-slate-700">
                <p className="text-[10px] text-slate-400 dark:text-gray-400 uppercase tracking-wider">{c.label}</p>
                <p className={cn('font-mono font-bold text-sm mt-1', c.color)}>{c.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="relative mt-4 max-w-xs">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Filter by supplier…"
            className="w-full pl-9 pr-3 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center h-48"><Spinner size="md" /></div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-400 dark:text-gray-400">
            <Building size={40} className="mb-3 opacity-30" />
            <p className="text-sm font-medium">No outstanding balances found</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-700/60 border-b border-slate-200 dark:border-slate-700 text-xs text-slate-500 dark:text-gray-400 uppercase tracking-wider">
                  <th className="px-4 py-3 text-left font-semibold">Supplier</th>
                  <th className="px-4 py-3 text-left font-semibold">Fin Year</th>
                  <th className="px-4 py-3 text-left font-semibold">Currency</th>
                  <th className="px-4 py-3 text-right font-semibold">Original</th>
                  <th className="px-4 py-3 text-right font-semibold">Allocated</th>
                  <th className="px-4 py-3 text-right font-semibold">Outstanding</th>
                  <th className="px-4 py-3 text-right font-semibold">Items</th>
                  <th className="px-4 py-3 w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {filtered.map((row, i) => (
                  <tr
                    key={`${row.partyId}-${row.currencyCode}-${i}`}
                    onClick={() => handleRowClick(row)}
                    className="hover:bg-blue-50 dark:hover:bg-slate-700/40 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 dark:text-white text-xs">{row.partyName}</div>
                      <div className="text-[11px] text-slate-400 dark:text-gray-400">{row.partyCode}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 dark:text-gray-400 font-mono">{row.financialYearCode}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 dark:text-gray-400 font-mono">{row.currencyCode}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-slate-700 dark:text-slate-300 whitespace-nowrap">
                      {fmt(row.totalOriginal)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-emerald-700 dark:text-emerald-400 whitespace-nowrap">
                      {fmt(row.totalAllocated)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs font-semibold whitespace-nowrap">
                      <span className={row.totalOutstanding > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-slate-400 dark:text-gray-400'}>
                        {fmt(row.totalOutstanding)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-slate-500 dark:text-gray-400">{row.openItemCount}</td>
                    <td className="px-4 py-3 text-slate-400 dark:text-gray-400">
                      <ChevronRight size={14} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/40">
                  <td className="px-4 py-3">
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                      Total ({filtered.length})
                    </span>
                  </td>
                  <td colSpan={2} />
                  <td className="px-4 py-3 text-right font-mono text-sm font-bold text-slate-700 dark:text-slate-300 whitespace-nowrap">
                    {fmt(totals.totalOriginal)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-sm font-bold text-emerald-700 dark:text-emerald-400 whitespace-nowrap">
                    {fmt(totals.totalAllocated)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-sm font-bold whitespace-nowrap">
                    <span className={totals.totalOutstanding > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-slate-400 dark:text-gray-400'}>
                      {fmt(totals.totalOutstanding)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-xs font-bold text-slate-500 dark:text-gray-400">{totals.openItemCount}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
